// Counter sales — record a PERSONAL sale (direct sale by the admin) or type
// in a WALK-IN entry, and browse everything entered. Also the admin's entry
// desk for SUPERVISOR direct sales (one tap prefills the form; saving marks
// the reminder entered) and a read-only view of what the salespersons sold
// today. Shared by the superadmin panel and the ops-admin workspace (api prop).
//
// The form mirrors the SALES site's inquiry form: one vehicle search box with
// a rich results dropdown (+ manual entry fallback), the fuel/transmission
// variant picker, a parts search with quick-add chips and part cards, and a
// full/advance payment section.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FiCheck,
  FiCheckCircle,
  FiChevronDown,
  FiClock,
  FiCreditCard,
  FiEdit3,
  FiMinus,
  FiPackage,
  FiPlus,
  FiRefreshCw,
  FiSearch,
  FiShoppingBag,
  FiTrash2,
  FiTrendingUp,
  FiTruck,
  FiUser,
  FiX,
} from 'react-icons/fi';
import { Alert, Button } from '../common/index.js';
import { useOpsDay } from '../../hooks/useOpsDay';
// Exchange eligibility — SAME rule as the backend (ExchangeEligiblePart.matches)
// and the superadmin's sale editor: one list, one matcher.
import { activeExchangeNames, isExchangeEligible } from '../../utils/exchangeEligibility';
import { PAYMENT_MODES, isKnownMode, modeLabel } from '../../constants/paymentModes';

const formatPrice = (value) => {
  if (value == null || value === '') return '';
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
};

// API dates come as YYYY-MM-DD; the book reads DD-MM-YYYY.
const formatDay = (value) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ''));
  return m ? `${m[3]}-${m[2]}-${m[1]}` : (value || '');
};

const TYPE_META = {
  personal: { label: 'Personal', bg: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-200' },
  walkin: { label: 'Walk-in', bg: 'bg-sky-50', text: 'text-sky-700', border: 'border-sky-200' },
};

// Same variant codes the sales site and the walk-in entry app store.
const VARIANTS = [
  { code: 'pmt', label: 'Petrol Manual', fuel: 'Petrol' },
  { code: 'pat', label: 'Petrol Automatic', fuel: 'Petrol' },
  { code: 'dmt', label: 'Diesel Manual', fuel: 'Diesel' },
  { code: 'dat', label: 'Diesel Automatic', fuel: 'Diesel' },
  { code: 'ev', label: 'Electric (EV)', fuel: 'Electric' },
];

const PAY_MODES = PAYMENT_MODES;

const PREFERRED_PARTS = [
  'Engine', 'Gearbox', 'Brake Pad (Front)', 'Battery',
  'Alternator', 'Radiator', 'Headlight Bulb', 'Side Mirror',
];

// Turn a pasted list into clean part names — same behaviour as the sales
// site's parts builder ("1. ", "10)", "- ", "•" prefixes stripped).
const parseBulkParts = (text) => (
  String(text)
    .split(/\r?\n/)
    .map((line) => (
      line
        .replace(/^[\s.]*(?:\d+[.)]|[-•*·–—])\s*/, '')
        .replace(/\s+/g, ' ')
        .trim()
    ))
    .filter(Boolean)
);

const sectionLabel = 'border-b border-slate-100 pb-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-400';
const inputClass = 'w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-300';

// ── Vehicle picker — one search box, rich dropdown, variant + year ──────────
const VehiclePicker = ({ catalog, brand, model, variant, year, onSet }) => {
  const searchRef = useRef(null);
  const variantRef = useRef(null);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [variantOpen, setVariantOpen] = useState(false);
  const [manual, setManual] = useState(false);
  const [manualBrand, setManualBrand] = useState('');
  const [manualModel, setManualModel] = useState('');
  const [manualError, setManualError] = useState('');

  useEffect(() => {
    const close = (e) => {
      if (!searchRef.current?.contains(e.target)) setOpen(false);
      if (!variantRef.current?.contains(e.target)) setVariantOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const vehicles = useMemo(() => (
    (catalog?.brands || []).flatMap((b) => (b.models || []).map((m) => ({ brand: b.name, model: m.name })))
  ), [catalog]);

  const results = useMemo(() => {
    const nq = query.trim().toLowerCase();
    if (nq.length < 2) return [];
    return vehicles.filter((v) => `${v.brand} ${v.model}`.toLowerCase().includes(nq)).slice(0, 10);
  }, [vehicles, query]);

  const selected = brand && model;
  const selectedVariant = VARIANTS.find((v) => v.code === variant);

  const choose = (v) => {
    onSet({ brand: v.brand, model: v.model });
    setQuery('');
    setOpen(false);
    setManual(false);
  };

  const startManual = (text = '') => {
    // Split "Volkswagen Polo" on a known brand where possible, like sales does.
    const normalized = text.trim();
    const lower = normalized.toLowerCase();
    const brands = [...(catalog?.brands || [])].sort((a, b) => b.name.length - a.name.length);
    const match = brands.find((b) => lower === b.name.toLowerCase() || lower.startsWith(`${b.name.toLowerCase()} `));
    if (match) {
      setManualBrand(match.name);
      setManualModel(normalized.slice(match.name.length).trim());
    } else {
      const words = normalized.split(/\s+/).filter(Boolean);
      setManualBrand(words.length > 1 ? words[0] : '');
      setManualModel(words.length > 1 ? words.slice(1).join(' ') : normalized);
    }
    setManualError('');
    setManual(true);
    setOpen(false);
  };

  const confirmManual = () => {
    if (!manualBrand.trim() || !manualModel.trim()) {
      setManualError('Enter both the car brand and model');
      return;
    }
    onSet({ brand: manualBrand.trim(), model: manualModel.trim() });
    setManual(false);
    setManualError('');
    setQuery('');
  };

  const clearVehicle = () => {
    onSet({ brand: '', model: '', variant: '' });
    setQuery('');
  };

  return (
    <div className="space-y-3">
      <p className={sectionLabel}>Car <span className="normal-case font-normal">(optional)</span></p>

      {!selected && !manual && (
        <div ref={searchRef} className="relative">
          <div className="flex items-center rounded-xl border border-slate-200 bg-slate-50 px-3 transition focus-within:border-violet-400 focus-within:bg-white focus-within:ring-2 focus-within:ring-violet-200">
            <FiSearch className="h-4 w-4 shrink-0 text-slate-400" />
            <input
              value={query}
              onFocus={() => setOpen(true)}
              onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
              onKeyDown={(e) => {
                if (e.key !== 'Enter' || query.trim().length < 2) return;
                e.preventDefault();
                if (results.length > 0) choose(results[0]);
                else startManual(query);
              }}
              autoComplete="off"
              placeholder="Search Volkswagen, Polo, Seltos…"
              className="min-w-0 flex-1 bg-transparent px-3 py-3 text-sm text-slate-900 outline-none placeholder:text-slate-400"
            />
            {query && (
              <button type="button" onClick={() => setQuery('')} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600" aria-label="Clear vehicle search">
                <FiX className="h-4 w-4" />
              </button>
            )}
          </div>

          {open && query.trim().length >= 2 && (
            <div className="absolute z-30 mt-2 max-h-72 w-full overflow-y-auto rounded-2xl border border-slate-100 bg-white p-2 shadow-xl shadow-slate-200/70">
              {results.map((v) => (
                <button
                  type="button"
                  key={`${v.brand}-${v.model}`}
                  onClick={() => choose(v)}
                  className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left hover:bg-violet-50"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-500"><FiTruck className="h-4 w-4" /></span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold text-slate-900">{v.model}</span>
                    <span className="block truncate text-xs text-slate-500">{v.brand}</span>
                  </span>
                  <FiCheck className="h-4 w-4 text-violet-500" />
                </button>
              ))}
              {results.length === 0 && (
                <div className="rounded-xl border border-dashed border-violet-200 bg-violet-50/50 p-4 text-center">
                  <p className="text-xs font-medium text-slate-700">No matching vehicle found</p>
                  <button type="button" onClick={() => startManual(query)} className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-violet-600 px-3 py-2 text-xs font-semibold text-white transition hover:bg-violet-700">
                    <FiPlus className="h-3.5 w-3.5" /> Add brand and model
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="mt-1.5 flex items-center justify-between gap-3">
            <p className="text-[11px] text-slate-400">Type at least 2 characters to search the catalogue.</p>
            <button type="button" onClick={() => startManual(query)} className="shrink-0 text-[11px] font-semibold text-violet-600 hover:text-violet-700">Enter manually</button>
          </div>
        </div>
      )}

      {manual && !selected && (
        <div className="rounded-2xl border border-violet-200 bg-gradient-to-br from-violet-50 to-white p-4">
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm font-semibold text-violet-950">Add a new vehicle</p>
            <button type="button" onClick={() => { setManual(false); setManualError(''); }} className="shrink-0 rounded-lg px-2.5 py-1.5 text-[11px] font-semibold text-violet-600 hover:bg-violet-100">Use search</button>
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <input
              value={manualBrand}
              onChange={(e) => { setManualBrand(e.target.value); setManualError(''); }}
              className={inputClass}
              placeholder="Car brand"
              autoFocus
            />
            <input
              value={manualModel}
              onChange={(e) => { setManualModel(e.target.value); setManualError(''); }}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); confirmManual(); } }}
              className={inputClass}
              placeholder="Car model"
            />
          </div>
          {manualError && <p className="mt-2 text-xs font-medium text-rose-600">{manualError}</p>}
          <button type="button" onClick={confirmManual} className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-violet-700">
            <FiCheck className="h-4 w-4" /> Use this vehicle
          </button>
        </div>
      )}

      {selected && (
        <div className="flex items-center gap-3 rounded-2xl border border-emerald-100 bg-emerald-50/60 p-3.5">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-emerald-600 shadow-sm"><FiTruck className="h-4 w-4" /></span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-slate-900">{model}</p>
            <p className="mt-0.5 truncate text-xs text-slate-500">{brand}</p>
          </div>
          <button type="button" onClick={clearVehicle} className="rounded-lg px-2.5 py-1.5 text-xs font-semibold text-slate-500 hover:bg-white hover:text-violet-600">Change</button>
        </div>
      )}

      {selected && (
        <div className="grid gap-3 sm:grid-cols-2">
          <div ref={variantRef} className="relative">
            <button
              type="button"
              onClick={() => setVariantOpen((o) => !o)}
              className={`flex w-full items-center gap-3 rounded-xl border px-3.5 py-2 text-left outline-none transition focus:ring-2 focus:ring-violet-200 ${variantOpen ? 'border-violet-400 bg-white' : 'border-slate-200 bg-slate-50 hover:border-slate-300 hover:bg-white'}`}
              aria-haspopup="listbox"
              aria-expanded={variantOpen}
            >
              <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-xs font-bold ${selectedVariant?.fuel === 'Diesel' ? 'bg-amber-50 text-amber-700' : selectedVariant ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-400'}`}>
                {selectedVariant ? selectedVariant.fuel.charAt(0) : '—'}
              </span>
              <span className="min-w-0 flex-1">
                <span className={`block truncate text-sm font-semibold ${selectedVariant ? 'text-slate-900' : 'text-slate-400'}`}>
                  {selectedVariant?.label || 'Fuel & transmission'}
                </span>
                <span className="block text-[10px] uppercase tracking-wider text-slate-400">
                  {selectedVariant ? selectedVariant.code : 'Optional'}
                </span>
              </span>
              <FiChevronDown className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${variantOpen ? 'rotate-180' : ''}`} />
            </button>

            {variantOpen && (
              <div className="absolute z-30 mt-2 w-full overflow-hidden rounded-2xl border border-slate-100 bg-white p-2 shadow-xl shadow-slate-200/70" role="listbox">
                {VARIANTS.map((v) => {
                  const isSel = v.code === variant;
                  const diesel = v.fuel === 'Diesel';
                  return (
                    <button
                      type="button"
                      key={v.code}
                      role="option"
                      aria-selected={isSel}
                      onClick={() => { onSet({ variant: isSel ? '' : v.code }); setVariantOpen(false); }}
                      className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition ${isSel ? 'bg-violet-50' : 'hover:bg-slate-50'}`}
                    >
                      <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-xs font-bold ${diesel ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700'}`}>
                        {v.fuel.charAt(0)}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-semibold text-slate-900">{v.label}</span>
                        <span className="block text-[11px] text-slate-500">{v.fuel}</span>
                      </span>
                      {isSel ? <FiCheck className="h-4 w-4 text-violet-600" /> : <span className="text-[10px] font-semibold uppercase text-slate-300">{v.code}</span>}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <input
            type="text"
            inputMode="numeric"
            maxLength={4}
            value={year}
            onChange={(e) => onSet({ year: e.target.value.replace(/\D/g, '').slice(0, 4) })}
            className={inputClass}
            placeholder="Model year (e.g. 2019)"
            autoComplete="off"
          />
        </div>
      )}
    </div>
  );
};

// ── Parts picker — search + quick-add chips + part cards (qty & price) ──────
const PartsPicker = ({ suggestions, items, setItems, exchangeNames = [], flagMissingSource = false }) => {
  const rootRef = useRef(null);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const close = (e) => { if (!rootRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const selectedNames = useMemo(() => new Set(items.map((it) => it.name.toLowerCase())), [items]);

  const quickParts = useMemo(() => {
    const preferred = PREFERRED_PARTS.filter((n) => suggestions.includes(n));
    return [...preferred, ...suggestions.filter((n) => !preferred.includes(n))].slice(0, 8);
  }, [suggestions]);

  const filtered = useMemo(() => {
    const nq = query.trim().toLowerCase();
    if (!nq) return quickParts;
    return suggestions.filter((n) => n.toLowerCase().includes(nq)).slice(0, 10);
  }, [query, quickParts, suggestions]);

  const exactMatch = suggestions.some((n) => n.toLowerCase() === query.trim().toLowerCase());

  const addPart = (name) => {
    const clean = name.trim();
    if (!clean) return;
    setItems((prev) => (
      prev.some((it) => it.name.toLowerCase() === clean.toLowerCase())
        ? prev
        : [...prev, { name: clean, quantity: '1', unit_price: '', source: '', is_exchange: false, exchange_received: true }]
    ));
    setQuery('');
    setOpen(false);
  };

  const removePart = (name) => {
    setItems((prev) => prev.filter((it) => it.name.toLowerCase() !== name.toLowerCase()));
  };

  const updatePart = (index, field, value) => {
    setItems((prev) => prev.map((it, i) => (i === index ? { ...it, [field]: value } : it)));
  };

  // Paste a whole list at once — one part per line, numbering stripped.
  const handlePaste = (e) => {
    const names = parseBulkParts(e.clipboardData?.getData('text') ?? '');
    if (names.length < 2) return;
    e.preventDefault();
    names.forEach(addPart);
  };

  return (
    <div>
      <p className={`${sectionLabel} mb-2`}>Items *</p>

      <div ref={rootRef} className="relative">
        <div className="flex items-stretch gap-2">
          <div className="flex min-w-0 flex-1 items-center rounded-xl border border-slate-200 bg-slate-50 px-3 transition focus-within:border-violet-400 focus-within:bg-white focus-within:ring-2 focus-within:ring-violet-200">
            <FiSearch className="h-4 w-4 shrink-0 text-slate-400" />
            <input
              value={query}
              onFocus={() => setOpen(true)}
              onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addPart(query); } }}
              onPaste={handlePaste}
              placeholder="Search parts, type a custom name, or paste a list"
              className="min-w-0 flex-1 bg-transparent px-3 py-3 text-sm text-slate-900 outline-none placeholder:text-slate-400"
              autoComplete="off"
            />
            {query && (
              <button type="button" onClick={() => setQuery('')} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600" aria-label="Clear part search">
                <FiX className="h-4 w-4" />
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={() => addPart(query)}
            disabled={!query.trim()}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-xl bg-violet-600 px-4 text-sm font-semibold text-white transition hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <FiPlus className="h-4 w-4" /> <span className="hidden sm:inline">Add</span>
          </button>
        </div>

        {open && (
          <div className="absolute z-30 mt-2 max-h-72 w-full overflow-y-auto rounded-2xl border border-slate-100 bg-white p-2 shadow-xl shadow-slate-200/70">
            {filtered.map((partName) => {
              const isSel = selectedNames.has(partName.toLowerCase());
              return (
                <button type="button" key={partName} onClick={() => addPart(partName)} className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition hover:bg-violet-50">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500"><FiPackage className="h-4 w-4" /></span>
                  <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-800">{partName}</span>
                  {isSel
                    ? <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-600"><FiCheck className="h-3.5 w-3.5" /> Added</span>
                    : <FiPlus className="h-4 w-4 text-violet-500" />}
                </button>
              );
            })}
            {query.trim() && !exactMatch && (
              <button type="button" onClick={() => addPart(query)} className="mt-1 flex w-full items-center gap-3 rounded-xl border border-dashed border-violet-200 bg-violet-50/60 px-3 py-3 text-left text-violet-700 transition hover:bg-violet-50">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white"><FiPlus className="h-4 w-4" /></span>
                <span className="min-w-0 flex-1 text-xs">Add custom part <strong className="block truncate text-sm">“{query.trim()}”</strong></span>
              </button>
            )}
            {!query.trim() && suggestions.length === 0 && (
              <p className="px-3 py-5 text-center text-xs text-slate-500">Catalogue is loading. You can still type a custom part.</p>
            )}
          </div>
        )}
      </div>

      {quickParts.length > 0 && (
        <div className="mt-3">
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">Quick add</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {quickParts.map((partName) => {
              const isSel = selectedNames.has(partName.toLowerCase());
              return (
                <button
                  type="button"
                  key={partName}
                  title={isSel ? 'Tap to remove' : undefined}
                  onClick={() => (isSel ? removePart(partName) : addPart(partName))}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition ${isSel ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-white text-slate-600 hover:border-violet-200 hover:bg-violet-50 hover:text-violet-700'}`}
                >
                  {isSel ? <FiCheck className="h-3 w-3" /> : <FiPlus className="h-3 w-3" />}
                  {partName}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-4">
        <div className="mb-2 flex items-center justify-between gap-3">
          <p className="text-xs font-semibold text-slate-600">Selected parts</p>
          <span className="rounded-full bg-slate-100 px-2 py-1 text-[10px] font-semibold text-slate-600">{items.length}</span>
        </div>
        {items.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/60 px-5 py-7 text-center">
            <FiPackage className="mx-auto h-6 w-6 text-slate-300" />
            <p className="mt-2 text-sm font-medium text-slate-600">No parts selected</p>
            <p className="mt-1 text-xs text-slate-400">Search above or tap a quick-add option.</p>
          </div>
        ) : (
          <div className="grid gap-2.5 grid-cols-[repeat(auto-fill,minmax(220px,1fr))]">
            {items.map((part, i) => (
              <article key={part.name.toLowerCase()} className="flex flex-col rounded-xl border border-slate-200 bg-white p-3 transition hover:border-violet-200 hover:shadow-sm">
                <div className="flex items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-slate-900" title={part.name}>{part.name}</p>
                    {!suggestions.includes(part.name) && <p className="text-[10px] font-medium text-amber-600">Custom part</p>}
                  </div>
                  <button
                    type="button"
                    onClick={() => removePart(part.name)}
                    className="-mr-1 -mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-slate-300 transition hover:bg-rose-50 hover:text-rose-500"
                    aria-label={`Remove ${part.name}`}
                  >
                    <FiTrash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
                <div className="mt-2.5 flex items-center justify-between gap-2">
                  <div className="flex items-center rounded-lg border border-slate-200 bg-slate-50">
                    <button type="button" onClick={() => updatePart(i, 'quantity', String(Math.max(1, Number(part.quantity) - 1)))} className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-200 hover:text-slate-700" aria-label={`Decrease ${part.name} quantity`}><FiMinus className="h-3.5 w-3.5" /></button>
                    <span className="w-7 text-center text-xs font-semibold text-slate-800">{part.quantity}</span>
                    <button type="button" onClick={() => updatePart(i, 'quantity', String(Number(part.quantity) + 1))} className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-200 hover:text-slate-700" aria-label={`Increase ${part.name} quantity`}><FiPlus className="h-3.5 w-3.5" /></button>
                  </div>
                  <div className="relative w-28">
                    <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-xs font-semibold text-slate-400">₹</span>
                    <input
                      type="number"
                      min="0"
                      step="any"
                      inputMode="numeric"
                      value={part.unit_price}
                      onChange={(e) => updatePart(i, 'unit_price', e.target.value)}
                      placeholder="Price"
                      className="w-full rounded-lg border border-slate-200 py-1.5 pl-6 pr-2 text-xs font-semibold focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-200"
                    />
                  </div>
                </div>
                {/* Where the part came from. The CAP-vs-OSM split on the
                    superadmin's screens is only as true as this tap — it can
                    be ticked here or left for later, never guessed. */}
                <div className="mt-2 flex items-center gap-1.5">
                  <span className={`text-[10px] font-bold uppercase tracking-wide ${flagMissingSource && !part.source ? 'text-rose-500' : 'text-slate-400'}`}>
                    From{flagMissingSource && !part.source ? ' — pick one' : ''}
                  </span>
                  {[['cap', 'CAP'], ['osm', 'OSM']].map(([v, l]) => (
                    <button
                      key={v}
                      type="button"
                      title={v === 'cap' ? "CAP's own stock" : 'Open spare market'}
                      onClick={() => updatePart(i, 'source', part.source === v ? '' : v)}
                      className={`rounded-full border px-2.5 py-1 text-[10px] font-bold transition ${part.source === v ? 'border-sky-300 bg-sky-50 text-sky-700' : flagMissingSource && !part.source ? 'border-rose-300 bg-rose-50 text-rose-500 hover:border-rose-400' : 'border-slate-200 bg-white text-slate-400 hover:border-sky-200 hover:text-sky-600'}`}
                    >
                      {l}
                    </button>
                  ))}
                </div>
                {/* Only exchange-ELIGIBLE part types (the superadmin's
                    Exchange Parts list) get the exchange option. */}
                {isExchangeEligible(part.name, exchangeNames) && (
                  <>
                    <button
                      type="button"
                      onClick={() => updatePart(i, 'is_exchange', !part.is_exchange)}
                      title="Customer trades in their old part on this line"
                      className={`mt-2 inline-flex items-center gap-1 self-start rounded-full border px-2 py-1 text-[10px] font-bold transition ${part.is_exchange ? 'border-violet-300 bg-violet-50 text-violet-700' : 'border-slate-200 bg-white text-slate-400 hover:border-violet-200 hover:text-violet-600'}`}
                    >
                      <FiRefreshCw className="h-3 w-3" />
                      {part.is_exchange ? 'With exchange' : 'Mark as exchange'}
                    </button>
                    {part.is_exchange && (
                      <div className="mt-1.5 grid grid-cols-2 gap-1">
                        {[{ v: true, l: 'Old part taken' }, { v: false, l: 'To collect later' }].map((o) => {
                          const on = part.exchange_received !== false ? o.v === true : o.v === false;
                          return (
                            <button
                              key={String(o.v)}
                              type="button"
                              onClick={() => updatePart(i, 'exchange_received', o.v)}
                              className={`rounded-lg border px-1.5 py-1 text-[10px] font-bold transition ${on ? (o.v ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : 'border-amber-300 bg-amber-50 text-amber-700') : 'border-slate-200 bg-white text-slate-400 hover:border-slate-300'}`}
                            >
                              {o.l}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// The car catalog (brands→models + part names) is small and changes rarely —
// fetched once per session and shared across form opens. Same for the
// exchange-eligible part names (normalized, ready for isExchangeEligible).
let catalogCache = null;
let exchangeNamesCache = null;

const AddSaleForm = ({ api, onSaved, onClose, initial = null }) => {
  const [saleType, setSaleType] = useState(initial?.sale_type || 'personal');
  const [phone, setPhone] = useState(initial?.phone || '');
  const [name, setName] = useState(initial?.name || '');
  const [location, setLocation] = useState(initial?.location || '');
  const [carBrand, setCarBrand] = useState(initial?.carBrand || '');
  const [carModel, setCarModel] = useState(initial?.carModel || '');
  const [carVariant, setCarVariant] = useState(initial?.variant || '');
  const [carYear, setCarYear] = useState(initial?.carYear || '');
  const [items, setItems] = useState(initial?.items?.length ? initial.items : []);
  const [payType, setPayType] = useState(initial?.payment_type === 'advance' ? 'advance' : 'full');
  // A NEW sale starts at cash (what most counter sales are). An EXISTING one
  // starts at exactly what it says — including nothing, when the stored mode
  // is blank or legacy. Defaulting an edit to cash is how a GPay sale used to
  // turn into a cash sale just by being opened.
  const [payMode, setPayMode] = useState(initial ? (initial.payment_mode || '') : 'cash');
  const [advance, setAdvance] = useState(initial?.advance ?? '');
  const [total, setTotal] = useState(initial?.total ?? '');
  const [notes, setNotes] = useState(initial?.notes || '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  // Flipped on a save attempt with unattributed parts — from then on every
  // part still missing its CAP/OSM tap glows red until it gets one.
  const [flagMissingSource, setFlagMissingSource] = useState(false);
  const [catalog, setCatalog] = useState(catalogCache);
  const [exchangeNames, setExchangeNames] = useState(exchangeNamesCache || []);
  const [known, setKnown] = useState(null);   // phone lookup result
  const editing = Boolean(initial?.saleId);   // fixing an existing entry

  useEffect(() => {
    if (catalogCache) return undefined;
    let alive = true;
    api.catalog()
      .then((res) => {
        catalogCache = res.data ?? res;
        if (alive) setCatalog(catalogCache);
      })
      .catch(() => {});   // pickers degrade to plain typing
    return () => { alive = false; };
  }, [api]);

  // Refetched on EVERY form open (tiny list) — a part type the superadmin
  // adds to Exchange Parts must show its exchange option immediately, not
  // after a page refresh. The module cache only bridges the loading moment.
  useEffect(() => {
    let alive = true;
    api.exchangeParts()
      .then((res) => {
        exchangeNamesCache = activeExchangeNames((res.data ?? res)?.exchange_parts);
        if (alive) setExchangeNames(exchangeNamesCache);
      })
      .catch(() => {});   // fall back to the last fetched list; form still works
    return () => { alive = false; };
  }, [api]);

  // Phone → prefill, the same way the sales form works: once 10 digits are
  // typed the customer is looked up and their name/location fill themselves.
  // Skipped while editing — the customer is this sale's identity.
  useEffect(() => {
    if (editing) return undefined;
    const digits = phone.replace(/\D/g, '');
    if (digits.length !== 10) { setKnown(null); return undefined; }
    let alive = true;
    const t = setTimeout(() => {
      api.checkCustomer(phone.trim())
        .then((res) => {
          if (!alive) return;
          const data = res.data ?? res;
          setKnown(data);
          if (data.status === 'existing') {
            setName((cur) => cur || data.customer?.name || '');
            setLocation((cur) => cur || data.customer?.location || '');
          }
        })
        .catch(() => { if (alive) setKnown(null); });
    }, 400);
    return () => { alive = false; clearTimeout(t); };
  }, [api, phone, editing]);

  const partNames = catalog?.all_parts || [];

  const itemsTotal = items.reduce((sum, it) => {
    const qty = Number(it.quantity) || 0;
    const price = Number(it.unit_price);
    return Number.isFinite(price) ? sum + qty * price : sum;
  }, 0);
  const effectiveTotal = total !== '' ? Number(total) : itemsTotal;

  const setVehicle = (patch) => {
    if ('brand' in patch) setCarBrand(patch.brand);
    if ('model' in patch) setCarModel(patch.model);
    if ('variant' in patch) setCarVariant(patch.variant);
    if ('year' in patch) setCarYear(patch.year);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    const validItems = items.filter((it) => it.name.trim());
    const hasAnyPrice = validItems.some((it) => it.unit_price !== '' && Number(it.unit_price) >= 0);
    if (!phone.trim()) { setError('Customer phone number is required'); return; }
    if (validItems.length === 0) { setError('Add at least one item'); return; }
    // No sale saves with an unattributed part — the CAP/OSM split downstream
    // is built from this tap, and the backend refuses blanks too.
    const missingSource = validItems.filter((it) => !it.source);
    if (missingSource.length) {
      setFlagMissingSource(true);
      setError(`Pick CAP or OSM for: ${missingSource.map((it) => it.name).join(', ')}`);
      return;
    }
    // A supervisor sale often arrives with just the collected total — item
    // prices are optional as long as SOME amount is recorded.
    if (!hasAnyPrice && total === '') { setError('Add item prices or the total collected'); return; }
    if (payType === 'advance') {
      const adv = Number(advance);
      if (!Number.isFinite(adv) || adv <= 0) { setError('Enter the advance amount collected'); return; }
      if (effectiveTotal > 0 && adv >= effectiveTotal) {
        setError('An advance must be less than the total — record a full payment instead');
        return;
      }
    }

    setSubmitting(true);
    try {
      const body = {
        sale_type: saleType,
        car_brand: carBrand.trim(),
        car_model: carModel.trim(),
        car_variant: carVariant,
        car_year: carYear.trim(),
        items: validItems.map((it) => ({
          name: it.name.trim(),
          quantity: Number(it.quantity) || 1,
          unit_price: it.unit_price !== '' ? String(it.unit_price) : undefined,
          source: it.source || '',
          is_exchange: Boolean(it.is_exchange),
          exchange_received: it.is_exchange ? it.exchange_received !== false : true,
        })),
        total_amount: total !== '' ? String(total) : undefined,
        payment_type: payType,
        payment_mode: payMode || undefined,
        advance_amount: payType === 'advance' ? String(advance) : undefined,
        notes: notes.trim(),
      };
      if (editing) {
        // The customer is identity — never patched; everything else may be fixed.
        await api.update(initial.saleId, body);
      } else {
        await api.create({
          ...body,
          phone_number: phone.trim(),
          name: name.trim() || undefined,
          location: location.trim() || undefined,
        });
      }
      onSaved();
    } catch (err) {
      const data = err?.response?.data;
      const msg = data?.error
        || (data && typeof data === 'object' ? Object.entries(data).map(([k, v]) => `${k}: ${v}`).join(' · ') : null)
        || err.message || 'Could not save the sale';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="rounded-2xl border border-violet-200 bg-white p-5 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-base font-bold text-slate-900">{initial?.heading || 'Record a counter sale'}</h3>
        <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
          <FiX className="h-4 w-4" />
        </button>
      </div>

      <form onSubmit={submit} className="space-y-5">
        {error && <Alert type="error" message={error} />}

        {/* Sale type */}
        <div className="grid grid-cols-2 gap-3">
          {['personal', 'walkin'].map((t) => {
            const active = saleType === t;
            return (
              <button
                key={t}
                type="button"
                onClick={() => setSaleType(t)}
                className={`rounded-xl border-2 px-3 py-3 text-sm font-bold transition-all ${active ? 'border-violet-500 bg-violet-50 text-violet-700' : 'border-slate-200 bg-white text-slate-600 hover:border-violet-300'}`}
              >
                {t === 'personal' ? 'Personal sale (by admin)' : 'Walk-in entry'}
              </button>
            );
          })}
        </div>

        {/* Customer — phone first; a known number fills the rest itself */}
        <div className="space-y-3">
          <p className={sectionLabel}>
            Customer
            {editing && <span className="ml-2 normal-case font-semibold text-slate-400">can't be changed — delete &amp; re-enter for a wrong number</span>}
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-slate-700">Phone *</label>
              <input
                type="tel"
                inputMode="numeric"
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                placeholder="10-digit number"
                autoFocus={!initial}
                disabled={editing}
                className={`${inputClass} disabled:bg-slate-50 disabled:text-slate-400`}
              />
              {known?.status === 'existing' && (
                <p className="mt-1.5 inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-bold text-emerald-700">
                  <FiCheckCircle className="h-3 w-3" />
                  {known.customer?.name || 'Known customer'}
                  {known.walkin_stats?.total_purchases ? ` · ${known.walkin_stats.total_purchases} previous` : ''}
                </p>
              )}
              {known?.status === 'new' && (
                <p className="mt-1.5 text-[11px] font-semibold text-sky-600">New customer — type the name</p>
              )}
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-slate-700">Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Required for a new customer"
                disabled={editing}
                className={`${inputClass} disabled:bg-slate-50 disabled:text-slate-400`}
              />
            </div>
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-semibold text-slate-700">Location <span className="font-normal text-slate-400">(optional)</span></label>
            <input
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="e.g. Gandhipuram"
              disabled={editing}
              className={`${inputClass} disabled:bg-slate-50 disabled:text-slate-400`}
            />
          </div>
        </div>

        {/* Car — same search + variant picker as the sales inquiry form */}
        <VehiclePicker
          catalog={catalog}
          brand={carBrand}
          model={carModel}
          variant={carVariant}
          year={carYear}
          onSet={setVehicle}
        />

        {/* Items — search the catalogue, quick-add, per-part qty & price */}
        <PartsPicker
          suggestions={partNames}
          items={items}
          setItems={setItems}
          exchangeNames={exchangeNames}
          flagMissingSource={flagMissingSource}
        />

        {/* Payment — these deals are already priced: FULL or ADVANCE only */}
        <div className="space-y-3">
          <p className={sectionLabel}>Payment</p>
          <div className="grid grid-cols-2 gap-3">
            {[{ v: 'full', l: 'Full payment' }, { v: 'advance', l: 'Advance' }].map((t) => {
              const active = payType === t.v;
              return (
                <button
                  key={t.v}
                  type="button"
                  onClick={() => setPayType(t.v)}
                  className={`rounded-xl border-2 px-3 py-2.5 text-sm font-bold transition-all ${active ? 'border-violet-500 bg-violet-50 text-violet-700' : 'border-slate-200 bg-white text-slate-600 hover:border-violet-300'}`}
                >
                  {t.l}
                </button>
              );
            })}
          </div>

          <div className="flex flex-wrap gap-1.5">
            {PAY_MODES.map((m) => (
              <button
                key={m.v}
                type="button"
                onClick={() => setPayMode(m.v)}
                className={`rounded-lg border px-2.5 py-1.5 text-xs font-bold transition-all ${payMode === m.v ? 'border-violet-500 bg-violet-600 text-white' : 'border-slate-200 bg-white text-slate-600 hover:border-violet-300'}`}
              >
                {m.l}
              </button>
            ))}
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-slate-700">
                {payType === 'advance' ? 'Total (deal value)' : 'Total collected'}
              </label>
              <div className="relative">
                <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-sm font-semibold text-slate-400">₹</span>
                <input
                  type="number"
                  min="0"
                  step="any"
                  inputMode="numeric"
                  value={total}
                  onChange={(e) => setTotal(e.target.value)}
                  placeholder={itemsTotal > 0 ? `Auto: ${itemsTotal.toLocaleString('en-IN')}` : 'Auto-summed from items'}
                  className="w-full rounded-xl border border-slate-200 py-2.5 pl-8 pr-4 text-sm focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-300"
                />
              </div>
            </div>
            {payType === 'advance' ? (
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-slate-700">Advance collected *</label>
                <div className="relative">
                  <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-sm font-semibold text-slate-400">₹</span>
                  <input
                    type="number"
                    min="0"
                    step="any"
                    inputMode="numeric"
                    value={advance}
                    onChange={(e) => setAdvance(e.target.value)}
                    placeholder="Amount received now"
                    className="w-full rounded-xl border border-slate-200 py-2.5 pl-8 pr-4 text-sm focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-300"
                  />
                </div>
              </div>
            ) : (
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-slate-700">Notes</label>
                <input
                  type="text"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="e.g. old part exchanged"
                  className={inputClass}
                />
              </div>
            )}
          </div>
          {payType === 'advance' && (
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-slate-700">Notes</label>
              <input
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g. balance on delivery"
                className={inputClass}
              />
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3">
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button type="submit" variant="primary" loading={submitting} className="gap-2">
            <FiShoppingBag className="h-4 w-4" /> {editing ? 'Save changes' : 'Save sale'}
          </Button>
        </div>
      </form>
    </div>
  );
};

// One supervisor direct sale waiting to be entered into the day book.
const ReminderCard = ({ reminder, busy, onEnter, onMarkEntered }) => {
  const car = [reminder.car_brand, reminder.car_model, reminder.car_year].filter(Boolean).join(' ');
  const parts = (reminder.parts || [])
    .map((p) => `${p.name}${p.quantity > 1 ? ` ×${p.quantity}` : ''}`).join(', ');
  return (
    <div className="rounded-xl border border-amber-200 bg-white p-3.5">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-bold text-slate-900">
            {reminder.customer_name || 'Customer'}
            <span className="ml-2 text-xs font-semibold text-slate-400">{reminder.customer_phone}</span>
          </p>
          <p className="mt-0.5 text-xs text-slate-500">
            {car}{car && parts ? ' · ' : ''}{parts}
          </p>
          <p className="mt-1 text-[11px] font-semibold text-amber-700">
            By {reminder.supervisor_name} · {formatDay(reminder.sale_date)}
            {reminder.payment_type === 'advance' ? ' · advance' : ''}
            {reminder.payment_mode ? ` · ${modeLabel(reminder.payment_mode)}` : ''}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <span className="text-sm font-bold text-slate-800">{formatPrice(reminder.amount) || '—'}</span>
          <div className="flex gap-1.5">
            <button
              type="button"
              disabled={busy}
              onClick={() => onEnter(reminder)}
              className="inline-flex items-center gap-1 rounded-lg bg-amber-500 px-2.5 py-1.5 text-xs font-bold text-white transition-all hover:bg-amber-600 disabled:opacity-50"
            >
              <FiEdit3 className="h-3 w-3" /> Enter into book
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => onMarkEntered(reminder)}
              className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-bold text-slate-500 transition-all hover:border-emerald-300 hover:text-emerald-600 disabled:opacity-50"
              title="Already written down — just tick it off"
            >
              <FiCheckCircle className="h-3 w-3" /> Entered
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const SALES_STATUS_META = {
  dispatched: { label: 'Dispatched', cls: 'border-emerald-200 bg-emerald-50 text-emerald-700', Icon: FiCheckCircle },
  sold: { label: 'Paid', cls: 'border-violet-200 bg-violet-50 text-violet-700', Icon: FiCheckCircle },
  hold: { label: 'Awaiting confirm', cls: 'border-amber-200 bg-amber-50 text-amber-700', Icon: FiClock },
};

const CounterSales = ({ api }) => {
  const { refresh: refreshOpsDay } = useOpsDay();
  const [typeFilter, setTypeFilter] = useState('');   // '' = all
  const [q, setQ] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [adding, setAdding] = useState(false);
  const [day, setDay] = useState(null);               // /admin/ops/day/ payload
  const [prefill, setPrefill] = useState(null);       // form prefill (reminder / edit)
  const [linkedReminder, setLinkedReminder] = useState(null);
  const [busyReminder, setBusyReminder] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.list({ type: typeFilter || undefined, q: q || undefined });
      setData(res.data ?? res);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Could not load counter sales');
    } finally {
      setLoading(false);
    }
  }, [api, typeFilter, q]);

  const loadDay = useCallback(async () => {
    // Also nudges the shared ops feed so the nav badges stay in step
    refreshOpsDay();
    try {
      const res = await api.day();
      setDay(res.data ?? res);
    } catch (_) {}   // the day panels are additive — the page still works without them
  }, [api, refreshOpsDay]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadDay(); }, [loadDay]);

  const sales = data?.sales ?? [];
  const reminders = day?.supervisor_sales?.items ?? [];
  const salesToday = day?.sales;

  // Prefill the counter form from a supervisor sale — saving it marks the
  // reminder entered, so the queue empties itself as the book fills up.
  const enterReminder = (r) => {
    const mode = (r.payment_mode || '').trim().toLowerCase();
    setPrefill({
      heading: `Enter supervisor sale — ${r.supervisor_name}`,
      sale_type: 'personal',
      phone: r.customer_phone || '',
      name: r.customer_name || '',
      location: r.customer_location || '',
      carBrand: r.car_brand || '',
      carModel: r.car_model || '',
      variant: r.car_variant || '',
      carYear: r.car_year || '',
      items: (r.parts || []).map((p) => ({
        name: p.name || '', quantity: String(p.quantity || 1), unit_price: '',
        // The note carries one source for the whole sale; cap/osm prefill
        // every line ('stock' has no walk-in equivalent — left unticked).
        source: ['cap', 'osm'].includes(r.sale_source) ? r.sale_source : '',
        is_exchange: false,
      })),
      total: r.amount ?? '',
      payment_type: r.payment_type === 'advance' ? 'advance' : 'full',
      // The supervisor's mode carries straight through. It used to be
      // demoted to 'cash' with the truth pushed into a note — a GPay sale
      // entered as cash overstates the drawer by its whole value.
      payment_mode: isKnownMode(mode) ? mode : '',
      advance: r.advance_amount ?? '',
      notes: `Supervisor sale — ${r.supervisor_name}`
        + (r.payment_mode && !isKnownMode(mode) ? ` · paid by ${r.payment_mode}` : ''),
    });
    setLinkedReminder(r);
    setAdding(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const markEntered = async (r) => {
    setBusyReminder(r.id);
    try {
      await api.reminderEntered(r.id);
      loadDay();
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Could not update the reminder');
    } finally {
      setBusyReminder(null);
    }
  };

  const onSaved = async () => {
    setAdding(false);
    setPrefill(null);
    if (linkedReminder) {
      try { await api.reminderEntered(linkedReminder.id); } catch (_) {}
      setLinkedReminder(null);
    }
    load();
    loadDay();
  };

  // The customer finally brought the old part — tick the exchange line off.
  const markOldPartReceived = async (item) => {
    try {
      await api.itemExchangeReceived(item.id, true);
      load();
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Could not update the exchange line');
    }
  };

  // Fix a wrongly entered sale — same form, prefilled, saving PATCHes it.
  const editSale = (sale) => {
    setPrefill({
      saleId: sale.id,
      heading: `Edit counter sale — ${sale.customer?.name || 'customer'}`,
      sale_type: sale.sale_type || 'personal',
      phone: sale.customer?.phone_number || '',
      name: sale.customer?.name || '',
      location: sale.customer?.location || '',
      carBrand: sale.car_brand || '',
      carModel: sale.car_model || '',
      variant: sale.car_variant || '',
      carYear: sale.car_year || '',
      items: (sale.items || []).map((it) => ({
        name: it.name || '',
        quantity: String(it.quantity || 1),
        unit_price: it.unit_price != null ? String(Number(it.unit_price)) : '',
        source: it.source || '',
        is_exchange: Boolean(it.is_exchange),
        exchange_received: it.exchange_received !== false,
      })),
      total: sale.total_amount != null ? String(Number(sale.total_amount)) : '',
      payment_type: sale.payment_type === 'advance' ? 'advance' : 'full',
      // Never coerce: an unknown legacy mode comes through blank so the
      // admin picks the right one, rather than the form asserting 'cash'.
      payment_mode: isKnownMode(sale.payment_mode) ? sale.payment_mode : '',
      advance: sale.advance_amount != null ? String(Number(sale.advance_amount)) : '',
      notes: sale.notes || '',
    });
    setLinkedReminder(null);
    setAdding(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const deleteSale = async (sale) => {
    try {
      await api.remove(sale.id);
      setConfirmDeleteId(null);
      load();
      loadDay();
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Could not delete the sale');
    }
  };

  return (
    <div className="space-y-4">
      {error && <Alert type="error" message={error} />}

      {/* Supervisor direct sales waiting for their book entry */}
      {reminders.length > 0 && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-4">
          <div className="mb-3 flex items-center justify-between">
            <p className="text-sm font-bold text-amber-800">
              Supervisor sales to enter
              <span className="ml-2 rounded-full bg-amber-200/70 px-2 py-0.5 text-xs font-bold text-amber-800">
                {day.supervisor_sales.pending_count}
              </span>
            </p>
            <span className="text-xs font-bold text-amber-700">{formatPrice(day.supervisor_sales.pending_value)}</span>
          </div>
          <div className="space-y-2">
            {reminders.map((r) => (
              <ReminderCard
                key={r.id}
                reminder={r}
                busy={busyReminder === r.id}
                onEnter={enterReminder}
                onMarkEntered={markEntered}
              />
            ))}
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {[{ v: '', l: 'All' }, { v: 'personal', l: 'Personal' }, { v: 'walkin', l: 'Walk-in' }].map((t) => {
            const active = typeFilter === t.v;
            return (
              <button
                key={t.v}
                type="button"
                onClick={() => setTypeFilter(t.v)}
                className={`rounded-full border px-3.5 py-1.5 text-sm font-bold transition-all ${active ? 'border-violet-500 bg-violet-600 text-white shadow-sm' : 'border-slate-200 bg-white text-slate-600 hover:border-violet-300'}`}
              >
                {t.l}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <FiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Phone or name…"
              className="w-52 rounded-xl border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-300"
            />
          </div>
          <button
            type="button"
            onClick={() => { load(); loadDay(); }}
            disabled={loading}
            className="flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500 transition-colors hover:border-violet-300 hover:bg-violet-50 hover:text-violet-600 disabled:opacity-50"
            aria-label="Refresh"
            title="Refresh"
          >
            <FiRefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          {!adding && (
            <Button variant="primary" onClick={() => setAdding(true)} className="gap-2">
              <FiPlus className="h-4 w-4" /> New sale
            </Button>
          )}
        </div>
      </div>

      {adding && (
        <AddSaleForm
          key={prefill?.saleId ? `edit-${prefill.saleId}` : linkedReminder ? `reminder-${linkedReminder.id}` : 'blank'}
          api={api}
          initial={prefill}
          onSaved={onSaved}
          onClose={() => { setAdding(false); setPrefill(null); setLinkedReminder(null); }}
        />
      )}

      {/* Totals strip */}
      {data && (
        <div className="flex items-center gap-4 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm">
          <span className="font-semibold text-slate-500">{data.count} sale{data.count === 1 ? '' : 's'}</span>
          <span className="font-bold text-slate-800">Total {formatPrice(data.total_amount) || '₹0'}</span>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="py-16 text-center text-sm text-slate-400">Loading…</div>
      ) : sales.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-200 bg-white py-14 text-center">
          <FiShoppingBag className="mx-auto mb-2 h-8 w-8 text-slate-200" />
          <p className="text-sm text-slate-400">No counter sales recorded yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {sales.map((sale) => {
            const meta = TYPE_META[sale.sale_type] || TYPE_META.walkin;
            const variantLabel = VARIANTS.find((v) => v.code === sale.car_variant)?.label || sale.car_variant;
            const car = [sale.car_brand, sale.car_model, variantLabel, sale.car_year].filter(Boolean).join(' ');
            const isAdvance = sale.payment_type === 'advance';
            return (
              <div key={sale.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex min-w-0 items-start gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-50 text-slate-500">
                      <FiUser className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="truncate text-sm font-bold text-slate-900">{sale.customer?.name || 'Customer'}</h3>
                      <p className="text-xs text-slate-400">{sale.customer?.phone_number}{car ? ` · ${car}` : ''}</p>
                      {sale.items?.length > 0 && (
                        <p className="mt-1 text-xs text-slate-500">
                          {sale.items.map((it) => (
                            `${it.name}${it.quantity > 1 ? ` ×${it.quantity}` : ''}${it.source ? ` · ${it.source.toUpperCase()}` : ''}${it.is_exchange ? (it.exchange_received === false ? ' (exchange · old part pending)' : ' (exchange)') : ''}`
                          )).join(', ')}
                        </p>
                      )}
                      {sale.items?.some((it) => it.is_exchange && it.exchange_received === false) && (
                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                          {sale.items.filter((it) => it.is_exchange && it.exchange_received === false).map((it) => (
                            <button
                              key={it.id}
                              type="button"
                              onClick={() => markOldPartReceived(it)}
                              title="Tap once the customer hands over the old part"
                              className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-bold text-amber-700 transition hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700"
                            >
                              <FiRefreshCw className="h-3 w-3" /> {it.name}: old part to collect — tap when received
                            </button>
                          ))}
                        </div>
                      )}
                      {/* Payment state ALWAYS shows — a counter sale is paid
                          in full unless it was recorded as an advance. */}
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {isAdvance ? (
                          sale.balance_paid ? (
                            <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700">
                              <FiCheckCircle className="h-3 w-3" /> Advance · balance collected
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-bold text-amber-700">
                              <FiCreditCard className="h-3 w-3" /> Advance {formatPrice(sale.advance_amount)}
                            </span>
                          )
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700">
                            <FiCheckCircle className="h-3 w-3" /> Paid in full
                          </span>
                        )}
                        {sale.payment_mode && (
                          <span className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-bold text-slate-500">
                            {modeLabel(sale.payment_mode)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1.5">
                    <span className="text-sm font-bold text-slate-800">{formatPrice(sale.total_amount) || '—'}</span>
                    <span className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-bold ${meta.bg} ${meta.text} ${meta.border}`}>
                      {meta.label}
                    </span>
                    <span className="text-[11px] text-slate-400">{formatDay(sale.sold_at)} · by {sale.entered_by_name || '-'}</span>
                    {confirmDeleteId === sale.id ? (
                      <div className="flex items-center gap-1.5">
                        <span className="text-[11px] font-bold text-rose-600">Delete this sale?</span>
                        <button
                          type="button"
                          onClick={() => deleteSale(sale)}
                          className="rounded-lg bg-rose-600 px-2 py-1 text-[11px] font-bold text-white hover:bg-rose-700"
                        >
                          Yes
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmDeleteId(null)}
                          className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-[11px] font-bold text-slate-500 hover:bg-slate-50"
                        >
                          No
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => editSale(sale)}
                          className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2 py-1 text-[11px] font-bold text-slate-500 transition hover:border-violet-300 hover:text-violet-600"
                        >
                          <FiEdit3 className="h-3 w-3" /> Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmDeleteId(sale.id)}
                          className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2 py-1 text-[11px] font-bold text-slate-400 transition hover:border-rose-300 hover:text-rose-600"
                          aria-label="Delete sale"
                        >
                          <FiTrash2 className="h-3 w-3" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* What the SALESPERSONS sold today — read-only reference so the admin
          sees the whole day, not just the counter. */}
      {salesToday && (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <p className="inline-flex items-center gap-2 text-sm font-bold text-slate-900">
              <FiTrendingUp className="h-4 w-4 text-violet-500" /> Salesperson sales today
            </p>
            <p className="text-xs font-semibold text-slate-500">
              {salesToday.count} paid · {formatPrice(salesToday.value) || '₹0'}
              {salesToday.hold_count > 0 && (
                <span className="ml-2 text-amber-600">{salesToday.hold_count} awaiting confirm · {formatPrice(salesToday.hold_value)}</span>
              )}
            </p>
          </div>
          {salesToday.orders.length === 0 ? (
            <p className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-3 py-3 text-center text-xs text-slate-400">
              No salesperson sales yet today
            </p>
          ) : (
            <div className="divide-y divide-slate-100">
              {salesToday.orders.map((o) => {
                const meta = SALES_STATUS_META[o.status] || SALES_STATUS_META.sold;
                return (
                  <div key={`${o.inquiry_id}-${o.status}`} className="flex flex-wrap items-center justify-between gap-2 py-2.5">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-slate-800">
                        {o.customer_name || 'Customer'}
                        <span className="ml-2 text-xs font-medium text-slate-400">by {o.salesperson_name || '-'}</span>
                      </p>
                      <p className="text-[11px] text-slate-400">{o.car}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {o.balance_open && (
                        <span className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-[10px] font-bold text-rose-600">
                          <FiCreditCard className="h-3 w-3" /> Balance due
                        </span>
                      )}
                      <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold ${meta.cls}`}>
                        <meta.Icon className="h-3 w-3" /> {meta.label}
                      </span>
                      <span className="w-20 text-right text-sm font-bold text-slate-800">{formatPrice(o.amount) || '—'}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CounterSales;
